import { Injectable } from '@angular/core';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';
import { from, Subject } from 'rxjs';
import { ApiService } from './api.service';
import { LogService } from './log.service';
import { ModalService } from './modal.service';
import { StorageService } from './storage.service';
import { ConfigService } from './config.service';
import { UserService } from './user.service';
import { environment } from 'environments/environment';

/**
 * Firebase Cloud Messaging integration for the WellTrack web app.
 *
 * Foreground messages: handled here via `@angular/fire/messaging`'s
 * modular `onMessage` API.
 *
 * Background messages: handled by `src/firebase-messaging-sw.js`,
 * which is registered with the Firebase config passed as URL query
 * params so `environment.firebaseConfig` stays the single source of
 * truth — no values are duplicated in the SW file.
 */
@Injectable()
export class MessagingService {
    /** Emits the parsed payload of every foreground push message. */
    readonly currentMessage = new Subject<unknown>();

    /** Emits the FCM device token whenever a new one is acquired. */
    readonly fcmToken = new Subject<string>();

    constructor(
        private messaging: Messaging,
        private storage: StorageService,
        private modalService: ModalService,
        private api: ApiService,
        private log: LogService,
        private configService: ConfigService,
        private userService: UserService
    ) {
        this.listenForForegroundMessages();
    }

    /**
     * Prompts the user for notification permission, retrieves an FCM
     * token, and registers it with the WellTrack backend. Safe to call
     * repeatedly — no-ops if permission is denied or no vapidKey is
     * configured.
     */
    requestPermission(): void {
        const config = environment.firebaseConfig as {
            vapidKey?: string;
        } & typeof environment.firebaseConfig;
        const vapidKey = config?.vapidKey;
        if (!vapidKey) {
            this.log.warn(
                'FCM: no vapidKey in environment.firebaseConfig; skipping token request.'
            );
            return;
        }

        from(Notification.requestPermission()).subscribe(permission => {
            if (permission !== 'granted') {
                this.log.debug('FCM: notification permission not granted');
                return;
            }
            from(this.registerServiceWorker()).subscribe({
                next: registration => {
                    from(
                        getToken(this.messaging, {
                            vapidKey,
                            serviceWorkerRegistration: registration,
                        })
                    ).subscribe({
                        next: token => this.onNewToken(token),
                        error: err => this.handleTokenError(err),
                    });
                },
                error: err => this.handleTokenError(err),
            });
        });
    }

    /** Register a device token with the backend. */
    register(deviceToken?: string): void {
        if (!deviceToken) {
            return;
        }
        this.api
            .post('devices', {
                Token: deviceToken,
                ClientID: this.configService.apiClientId(),
            })
            .subscribe({
                next: () => {
                    this.log.trace('Device registered: ' + deviceToken);
                    this.userService.reloadUser();
                    this.storage.set('lastDeviceToken', deviceToken);
                },
                error: (error: { message?: string }) => {
                    this.log.critical(
                        'Error registering device: ' + deviceToken
                    );
                    this.log.critical(JSON.stringify(error?.message));
                    this.log.error(
                        'Error registering device: ' + deviceToken
                    );
                },
            });
    }

    /**
     * Deregister a device token from the backend (e.g. on logout or
     * when a new token supersedes an old one).
     */
    deregister(deviceToken?: string): void {
        const token = deviceToken || this.storage.get('lastDeviceToken');
        if (!token) {
            return;
        }
        this.api.delete('devices/' + token).subscribe({
            next: () => {
                this.log.trace('Device deregistered: ' + token);
                this.storage.set('lastDeviceToken', null);
            },
            error: (error: { message?: string }) => {
                this.log.critical(
                    'Error deregistering device: ' + (error?.message ?? '')
                );
                this.log.error('Error deregistering device: ' + token);
            },
        });
    }

    /** Clear the cached FCM token from local storage. */
    deleteFcmToken(): void {
        this.storage.set('fcmToken', null, true);
    }

    private onNewToken(token: string): void {
        if (!token) {
            this.log.warn('FCM: token request returned empty');
            return;
        }
        this.storage.set('fcmToken', token, true);
        this.fcmToken.next(token);
        const lastToken = this.storage.get('lastDeviceToken');
        if (lastToken && lastToken !== token) {
            this.deregister(lastToken);
        }
        this.register(token);
    }

    private handleTokenError(err: { message?: string } | unknown): void {
        const message =
            (err as { message?: string })?.message ?? String(err);
        this.modalService.showAlert(
            'Error',
            'Unable to get permission to notify: ' + message
        );
    }

    /**
     * Register the FCM service worker with the firebase config baked
     * into the URL query string. The SW reads those params on install
     * and uses them to initializeApp — so this Angular code is the
     * single source of truth for firebase config.
     */
    private registerServiceWorker(): Promise<ServiceWorkerRegistration> {
        if (!('serviceWorker' in navigator)) {
            return Promise.reject(
                new Error('Service workers are not supported in this browser')
            );
        }
        const cfg = environment.firebaseConfig;
        const params = new URLSearchParams({
            apiKey: cfg.apiKey ?? '',
            authDomain: cfg.authDomain ?? '',
            projectId: cfg.projectId ?? '',
            storageBucket: cfg.storageBucket ?? '',
            messagingSenderId: cfg.messagingSenderId ?? '',
            appId: cfg.appId ?? '',
        });
        return navigator.serviceWorker.register(
            `/firebase-messaging-sw.js?${params.toString()}`
        );
    }

    private listenForForegroundMessages(): void {
        onMessage(this.messaging, payload => {
            this.currentMessage.next(payload);
        });
    }
}
